import type { FC, KeyboardEvent } from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@material-ui/core';
import type { FilledTextFieldProps } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import type { SxProps } from '@material-ui/system';

interface InputFieldProps extends Omit<FilledTextFieldProps, 'variant'> {
  InputProps?: Record<string, any>;
  sx?: SxProps;
}

export const InputField: FC<InputFieldProps> = (props) => {
  const { InputProps, sx, ...other } = props;

  // A field whose value is "NA" is marked as not relevant: strike it through.
  // Driven by the value (which is persisted), so it survives a page refresh.
  // Typing "NA" strikes it; changing the text back un-strikes it.
  const isNA = String(other.value ?? '').trim().toUpperCase() === 'NA';

  // Centralized keyboard behavior for every text field in the app:
  //  - Enter (no Shift): prevent the newline, save the field (via blur) and move
  //    focus to the next editable field, skipping deactivation/action buttons
  //    and other non-editable elements.
  //  - Shift+Enter: keep the native newline (no save, no focus change).
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) {
      return;
    }
    event.preventDefault();
    const input = event.currentTarget;
    // Editable fields only (inputs/textareas/selects), visible, enabled and still
    // in the tab order — excludes the deactivation eye (tabIndex=-1) and MUI's
    // hidden textarea clone (aria-hidden + tabIndex=-1).
    const editables = Array.from(
      document.querySelectorAll<HTMLElement>('input, textarea, select')
    ).filter(
      (el) =>
        !(el as HTMLInputElement).disabled &&
        el.tabIndex !== -1 &&
        el.getAttribute('aria-hidden') !== 'true' &&
        el.offsetParent !== null
    );
    const idx = editables.indexOf(input);
    input.blur();
    if (idx > -1 && idx + 1 < editables.length) {
      editables[idx + 1].focus();
    }
  };

  return (
    <TextField
      inputProps={{
        onKeyDown: handleKeyDown,
        sx: {
          alignItems: 'center',
          display: 'flex',
          fontSize: 14,
          height: 'unset',
          lineHeight: 1.6,
          // px: 1.5,
          // py: 0.5,
          padding: "5px",
          backgroundColor: 'background.paper',
          textDecoration: isNA ? 'line-through' : 'none',
          '&.MuiInputBase-inputAdornedStart': {
            pl: 0
          },
          ...sx
        }
      }}
      variant="filled"
      InputLabelProps={{
        shrink: true,
        sx: {
          color: 'text.primary',
          fontSize: 14,
          fontWeight: 500,
          mb: 0.5,
          position: 'relative',
          transform: 'none',
          // textDecoration: 'line-through'
        }
      }}
      sx={{
        '& .MuiFilledInput-root': {
          backgroundColor: 'background.paper',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: other.value == '' ? 'primary.main' : 'neutral.300',
          borderRadius: 1,
          // boxShadow: other.value == '' ? '0px 1px 2px 0px rgba(230, 73, 45, 0.4)' : '0px 1px 2px 0px rgba(9, 30, 66, 0.08)',
          overflow: 'hidden',
          p: 0,
          transition: (theme) => theme.transitions.create([
            'border-color',
            'box-shadow'
          ]),
          '&:before': {
            borderBottom: 0
          },
          '&:hover': {
            backgroundColor: 'background.paper'
          },
          '&.Mui-focused': {
            backgroundColor: 'background.paper',
            boxShadow: (theme) => `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`
          },
          '&.Mui-disabled': {
            backgroundColor: 'action.disabledBackground',
            boxShadow: 'none',
            borderColor: alpha('#D6DBE1', 0.5)
          },
          '.MuiInputAdornment-root.MuiInputAdornment-positionStart.MuiInputAdornment-root:not(.MuiInputAdornment-hiddenLabel)': {
            mt: 0,
            ml: 1.5
          }
        },
        ...sx
      }}
      // eslint-disable-next-line react/jsx-no-duplicate-props
      InputProps={{
        ...InputProps,
        disableUnderline: true
      }}
      {...other}
    />
  );
};

InputField.propTypes = {
  InputProps: PropTypes.object,
  sx: PropTypes.object
};
